import constants from '../../../../constants';

const API_BASE = constants.baseApiHost;
const API_KEY = constants.apikey;

const getSession = () => {
  const raw = localStorage['session'];
  return raw && raw !== 'undefined' ? JSON.parse(raw) : null;
};

const getHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    ApiKey: API_KEY || '',
    'Content-Type': 'application/json',
    Authorization: session?.key ? `bearer ${session.key}` : ''
  };
};

const handleApiResponse = async (response: Response) => {
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

const toNumber = (value: unknown): number =>
  typeof value === 'number' ? value : Number(value) || 0;

/**
 * The aggregate endpoint returns the summed value directly (e.g. `129450.25`),
 * but tolerate `{ data }` / `{ value }` wrappers in case the shape varies.
 */
const extractAggregateValue = (json: unknown): number => {
  if (typeof json === 'number' || typeof json === 'string') {
    return toNumber(json);
  }
  if (json && typeof json === 'object') {
    const { data, value } = json as { data?: unknown; value?: unknown };
    if (data !== undefined && data !== null) {
      return toNumber(data);
    }
    if (value !== undefined && value !== null) {
      return toNumber(value);
    }
  }
  return 0;
};

type Id = string | number;

interface AccountScope {
  accountId: Id;
  organizationId: Id;
}

interface SearchFilter {
  key: string;
  operator: string;
  value: Id;
}

/**
 * Every data point is scoped to both the applicant's account and the charity
 * (organization). organizationId is mandatory: without it these figures would
 * span every charity on the platform, not just this one.
 */
const buildAccountFilters = ({
  accountId,
  organizationId
}: AccountScope): SearchFilter[] => [
  { key: 'accountId', operator: 'EqualTo', value: accountId },
  { key: 'organizationId', operator: 'EqualTo', value: organizationId }
];

/**
 * Builds a "Sum" aggregate URL, e.g.
 * /api/v2/page/aggregate?fieldName=raisedAmount&fieldAggregate=Sum
 *   &filters[0].key=accountId...&filters[1].key=organizationId...
 * Brackets are kept literal to match the backend filter format (see aggregateUrlExtension).
 */
const buildSumAggregateUrl = (
  entity: string,
  fieldName: string,
  filters: SearchFilter[]
): string => {
  const filterQuery = filters
    .map(
      (f, i) =>
        `&filters[${i}].key=${f.key}` +
        `&filters[${i}].operator=${f.operator}` +
        `&filters[${i}].value=${encodeURIComponent(String(f.value))}`
    )
    .join('');

  return (
    `${API_BASE}/api/v2/${entity}/aggregate` +
    `?fieldName=${fieldName}&fieldAggregate=Sum${filterQuery}`
  );
};

const fetchSumAggregate = async (
  entity: string,
  fieldName: string,
  scope: AccountScope
): Promise<number> => {
  const response = await fetch(
    buildSumAggregateUrl(entity, fieldName, buildAccountFilters(scope)),
    {
      method: 'GET',
      headers: getHeaders()
    }
  );
  const json = await handleApiResponse(response);
  return extractAggregateValue(json);
};

/** Fundraising total: sum of raisedAmount across the account's pages for this charity. */
export const fetchAccountFundraisingTotal = (
  accountId: Id,
  organizationId: Id
): Promise<number> =>
  fetchSumAggregate('page', 'raisedAmount', { accountId, organizationId });

/** Lifetime donations: sum of amountConverted across the account's donations for this charity. */
export const fetchAccountDonationsTotal = (
  accountId: Id,
  organizationId: Id
): Promise<number> =>
  fetchSumAggregate('donation', 'amountConverted', { accountId, organizationId });

interface EventAmbassadorSearchItem {
  event?: { name?: string | null } | null;
}

/**
 * Returns the de-duplicated event names from every Event Ambassador application
 * this account has made to the charity, regardless of application status.
 */
export const fetchAccountEventAmbassadorEventNames = async (
  accountId: Id,
  organizationId: Id
): Promise<string[]> => {
  const body = {
    page: 1,
    pageSize: 100,
    text: '',
    resultType: 'Full',
    baseObjectsOnly: false,
    filters: buildAccountFilters({ accountId, organizationId }),
    orderBy: {
      key: 'createdAtLocal',
      direction: 'desc'
    }
  };

  const response = await fetch(`${API_BASE}/api/v2/EventAmbassador/search`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body)
  });

  const json = await handleApiResponse(response);
  const list: EventAmbassadorSearchItem[] = json?.data?.list ?? [];

  const names = list
    .map((item) => item?.event?.name?.trim())
    .filter((name): name is string => Boolean(name));

  return Array.from(new Set(names));
};
